import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { GlobalProvider, useGlobalContext } from "./globalContext";
import ProtectedRoute from './components/ProtectedRoute';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import { TourProvider } from './components/TourProvider';
import modelPreloader from './services/modelPreloader';
import performanceMonitor from './utils/performanceMonitor';
import { logLogoutActivity } from './utils/activityLogger';

// Lazy load components for better performance
const TestPage = lazy(() => import('./components/testPage'));
const LoginPage = lazy(() => import('./components/login'));
const CreateTest = lazy(() => import('./components/createTest'));
const SignUp = lazy(() => import('./components/signup'));
const ForgotPasswordPage = lazy(() => import('./components/forgotPassword'));
const ResetPage = lazy(() => import('./components/reset'));
const RedirectPage = lazy(() => import('./components/redirectPage'));
const SearchResult = lazy(() => import('./components/searchResult'));
const CreateTemplate = lazy(() => import('./components/createTemplate'));
const ProfilerPage = lazy(() => import('./components/profilerPage'));
const AIInterview = lazy(() => import('./components/aiinterview'));
const InterviewPage = lazy(() => import('./components/interviewPage'));
const SpeechToText = lazy(() => import('./components/speechtotext'));
const ChatBot = lazy(() => import('./components/chatBot'));
const FaceDetection = lazy(() => import('./components/faceDetection'));
const EditTemplate = lazy(() => import('./components/editTemplate'));
const Profile = lazy(() => import('./components/profile'));
const CreateJD = lazy(() => import('./components/createJD'));
const CreateTemplateFromJD = lazy(() => import('./components/createTemplateFromJD'));
const VerifyEmail = lazy(() => import('./components/verifyEmail'));
const SignupSuccess = lazy(() => import('./components/signupSuccess'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const DataProtectionPolicy = lazy(() => import('./components/DataProtectionPolicy'));
const EULA = lazy(() => import('./components/EULA'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const CloneTemplates = lazy(() => import('./components/cloneTemplates'));

// Loading component for Suspense fallback
const LoadingSpinner = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    flexDirection: 'column',
    gap: '16px'
  }}>
    <div style={{
      width: '40px',
      height: '40px',
      border: '4px solid #f3f3f3',
      borderTop: '4px solid #1CBBB4',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }}></div>
    <p style={{ color: '#666', fontSize: '14px' }}>Loading...</p>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

// Replace with your actual reCAPTCHA v3 site key
const RECAPTCHA_SITE_KEY = "6Lcb8jYsAAAAAGX87VEDrxMu8TZzAUL7jOwh9pqZ";

// Logout component that clears session and redirects
const LogoutHandler = () => {
  const { logout, globalValue, JWTValue } = useGlobalContext();
  const navigate = useNavigate();

  useEffect(() => {
    // Log logout activity
    logLogoutActivity(globalValue, 'logout_success', {
      status: 'success',
      timestamp: new Date().toISOString()
    }, JWTValue);
    
    logout();
    navigate('/login', { replace: true });
  }, [logout, navigate, globalValue, JWTValue]);

  return null;
};

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { globalValue } = useGlobalContext();
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [showProfilePopup, setShowProfilePopup] = React.useState(false);

  // Scroll detection for transparent navbar
  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Hide navbar on auth pages
  const authPages = ["/login", "/logout", "/signup", "/forgot-password", "/verify-email", "/signup-success", "/privacy-policy", "/data-protection-policy", "/eula"];
  const isAuthPage = authPages.some(route => location.pathname === route || location.pathname.startsWith(route + "/"));
  
  if (isAuthPage) {
    return null;
  }

  const routesToCheck = ["/test", "/redirectPage", "/reset"];
  const isResult = routesToCheck.some(route => location.pathname.includes(route));
  const loginCheck = ["/test"];
  const isLogin = loginCheck.some(route => location.pathname.includes(route));

  const navButtonStyle = {
    background: "transparent",
    border: "none",
    padding: "8px",
    marginTop: "10px",
    borderRadius: "50%",
    transition: "all 0.2s ease",
    minWidth: "44px",
    minHeight: "44px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer"
  };

  // Add hover effect via onMouseEnter/onMouseLeave since inline styles don't support :hover
  const handleNavBtnMouseEnter = (e) => {
    e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
    // Change SVG icon color to white on hover
    const paths = e.currentTarget.querySelectorAll('path');
    paths.forEach(path => {
      if (path.getAttribute('stroke')) {
        path.setAttribute('stroke', '#ffffff');
      }
      if (path.getAttribute('fill') && path.getAttribute('fill') !== 'none') {
        path.setAttribute('fill', '#ffffff');
      }
    });
    const circles = e.currentTarget.querySelectorAll('circle');
    circles.forEach(circle => {
      if (circle.getAttribute('stroke')) {
        circle.setAttribute('stroke', '#ffffff');
      }
      if (circle.getAttribute('fill') && circle.getAttribute('fill') !== 'none') {
        circle.setAttribute('fill', '#ffffff');
      }
    });
  };
  
  const handleNavBtnMouseLeave = (e) => {
    e.currentTarget.style.background = "transparent";
    // Reset SVG icon color
    const paths = e.currentTarget.querySelectorAll('path');
    paths.forEach(path => {
      if (path.getAttribute('stroke')) {
        path.setAttribute('stroke', '#1C274C');
      }
      if (path.getAttribute('fill') && path.getAttribute('fill') !== 'none') {
        path.setAttribute('fill', '#292D32');
      }
    });
    const circles = e.currentTarget.querySelectorAll('circle');
    circles.forEach(circle => {
      if (circle.getAttribute('stroke')) {
        circle.setAttribute('stroke', '#1C274C');
      }
      if (circle.getAttribute('fill') && circle.getAttribute('fill') !== 'none') {
        circle.setAttribute('fill', '#292D32');
      }
    });
  };

  return (
    <header
      role="banner"
      style={{
        position: "fixed",
        top: 0,
        width: "100%",
        background: isScrolled ? "rgba(255, 255, 255, 0.95)" : "#ffffff",
        color: "#1e293b",
        padding: "0px",
        borderRadius: "0px 0 0 0",
        textAlign: "center",
        display: "flex",
        justifyContent: "space-between",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
        borderBottom: "1px solid #e2e8f0",
        zIndex: 1000,
        transition: "all 0.3s ease"
      }}
    >
      <img
        src="../logo.png"
        alt="HRRobots - AI Interview Platform"
        style={{ padding: "5px", height: "50px", marginRight: "0px" }}
      />
      <nav role="navigation" aria-label="Main navigation" style={{ display: "flex", alignItems: "center", paddingRight: "15px", gap: "5px" }}>
        {isResult ? (
          <>
            {!isLogin && (
              <button 
                className="nav-btn"
                style={navButtonStyle} 
                onClick={() => navigate("/list")} 
                aria-label="Go to Dashboard" 
                title="Dashboard"
                onMouseEnter={handleNavBtnMouseEnter}
                onMouseLeave={handleNavBtnMouseLeave}
              >
                <svg width="30px" height="30px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                  <path d="M22 22L2 22" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M2 11L6.06296 7.74968M22 11L13.8741 4.49931C12.7784 3.62279 11.2216 3.62279 10.1259 4.49931L9.34398 5.12486" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M15.5 5.5V3.5C15.5 3.22386 15.7239 3 16 3H18.5C18.7761 3 19 3.22386 19 3.5V8.5" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M4 22V9.5" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M20 9.5V13.5M20 22V17.5" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M15 22V17C15 15.5858 15 14.8787 14.5607 14.4393C14.1213 14 13.4142 14 12 14C10.5858 14 9.87868 14 9.43934 14.4393M9 22V17" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 9.5C14 10.6046 13.1046 11.5 12 11.5C10.8954 11.5 10 10.6046 10 9.5C10 8.39543 10.8954 7.5 12 7.5C13.1046 7.5 14 8.39543 14 9.5Z" stroke="#1C274C" strokeWidth="1.5"/>
                </svg>
              </button>
            )}
          </>
        ) : (
          <>
            <button 
              className="nav-btn"
              style={navButtonStyle} 
              onClick={() => navigate("/list")} 
              aria-label="Go to Dashboard" 
              title="Dashboard"
              onMouseEnter={handleNavBtnMouseEnter}
              onMouseLeave={handleNavBtnMouseLeave}
            >
              <svg width="30px" height="30px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                <path d="M22 22L2 22" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M2 11L6.06296 7.74968M22 11L13.8741 4.49931C12.7784 3.62279 11.2216 3.62279 10.1259 4.49931L9.34398 5.12486" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M15.5 5.5V3.5C15.5 3.22386 15.7239 3 16 3H18.5C18.7761 3 19 3.22386 19 3.5V8.5" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M4 22V9.5" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M20 9.5V13.5M20 22V17.5" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M15 22V17C15 15.5858 15 14.8787 14.5607 14.4393C14.1213 14 13.4142 14 12 14C10.5858 14 9.87868 14 9.43934 14.4393M9 22V17" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 9.5C14 10.6046 13.1046 11.5 12 11.5C10.8954 11.5 10 10.6046 10 9.5C10 8.39543 10.8954 7.5 12 7.5C13.1046 7.5 14 8.39543 14 9.5Z" stroke="#1C274C" strokeWidth="1.5"/>
              </svg>
            </button>
            {globalValue && globalValue.toLowerCase() === 'saaimurugan@gmail.com' && (
              <button 
                className="nav-btn"
                style={navButtonStyle} 
                onClick={() => navigate("/admin")} 
                aria-label="Go to Admin Dashboard" 
                title="Admin Dashboard"
                onMouseEnter={handleNavBtnMouseEnter}
                onMouseLeave={handleNavBtnMouseLeave}
              >
                <svg width="30px" height="30px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" fill="#1C274C"/>
                </svg>
              </button>
            )}
            <div 
              style={{ position: "relative" }}
              onMouseEnter={() => setShowProfilePopup(true)}
              onMouseLeave={() => setShowProfilePopup(false)}
            >
              <button 
                className="nav-btn"
                style={navButtonStyle} 
                onClick={() => navigate("/profile")} 
                aria-label="View Profile" 
                aria-expanded={showProfilePopup} 
                title="Profile"
                onMouseEnter={handleNavBtnMouseEnter}
                onMouseLeave={handleNavBtnMouseLeave}
              >
                <svg width="30px" height="30px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                  <path d="M22 12C22 6.49 17.51 2 12 2C6.49 2 2 6.49 2 12C2 14.9 3.25 17.51 5.23 19.34C5.23 19.35 5.23 19.35 5.22 19.36C5.32 19.46 5.44 19.54 5.54 19.63C5.6 19.68 5.65 19.73 5.71 19.77C5.89 19.92 6.09 20.06 6.28 20.2C6.35 20.25 6.41 20.29 6.48 20.34C6.67 20.47 6.87 20.59 7.08 20.7C7.15 20.74 7.23 20.79 7.3 20.83C7.5 20.94 7.71 21.04 7.93 21.13C8.01 21.17 8.09 21.21 8.17 21.24C8.39 21.33 8.61 21.41 8.83 21.48C8.91 21.51 8.99 21.54 9.07 21.56C9.31 21.63 9.55 21.69 9.79 21.75C9.86 21.77 9.93 21.79 10.01 21.8C10.29 21.86 10.57 21.9 10.86 21.93C10.9 21.93 10.94 21.94 10.98 21.95C11.32 21.98 11.66 22 12 22C12.34 22 12.68 21.98 13.01 21.95C13.05 21.95 13.09 21.94 13.13 21.93C13.42 21.9 13.7 21.86 13.98 21.8C14.05 21.79 14.12 21.76 14.2 21.75C14.44 21.69 14.69 21.64 14.92 21.56C15 21.53 15.08 21.5 15.16 21.48C15.38 21.4 15.61 21.33 15.82 21.24C15.9 21.21 15.98 21.17 16.06 21.13C16.27 21.04 16.48 20.94 16.69 20.83C16.77 20.79 16.84 20.74 16.91 20.7C17.11 20.58 17.31 20.47 17.51 20.34C17.58 20.3 17.64 20.25 17.71 20.2C17.91 20.06 18.1 19.92 18.28 19.77C18.34 19.72 18.39 19.67 18.45 19.63C18.56 19.54 18.67 19.45 18.77 19.36C18.77 19.35 18.77 19.35 18.76 19.34C20.75 17.51 22 14.9 22 12ZM16.94 16.97C14.23 15.15 9.79 15.15 7.06 16.97C6.62 17.26 6.26 17.6 5.96 17.97C4.44 16.43 3.5 14.32 3.5 12C3.5 7.31 7.31 3.5 12 3.5C16.69 3.5 20.5 7.31 20.5 12C20.5 14.32 19.56 16.43 18.04 17.97C17.75 17.6 17.38 17.26 16.94 16.97Z" fill="#292D32"/>
                  <path d="M12 6.92969C9.93 6.92969 8.25 8.60969 8.25 10.6797C8.25 12.7097 9.84 14.3597 11.95 14.4197C11.98 14.4197 12.02 14.4197 12.04 14.4197C12.06 14.4197 12.09 14.4197 12.11 14.4197C12.12 14.4197 12.13 14.4197 12.13 14.4197C14.15 14.3497 15.74 12.7097 15.75 10.6797C15.75 8.60969 14.07 6.92969 12 6.92969Z" fill="#292D32"/>
                </svg>
              </button>
              {showProfilePopup && globalValue && (
                <div role="tooltip" style={{
                  position: "absolute",
                  top: "100%",
                  right: 0,
                  marginTop: "8px",
                  background: "white",
                  borderRadius: "12px",
                  padding: "12px 16px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                  whiteSpace: "nowrap",
                  zIndex: 1001,
                  color: "#333",
                  fontSize: "14px",
                  minWidth: "150px",
                  border: "1px solid #e2e8f0"
                }}>
                  <div style={{ fontWeight: "600", marginBottom: "4px", color: "#374151" }}>Logged in as</div>
                  <div style={{ color: "#475569" }}>{globalValue}</div>
                </div>
              )}
            </div>
            <button className="nav-btn" style={navButtonStyle} onClick={() => navigate("/logout")} onMouseEnter={handleNavBtnMouseEnter} onMouseLeave={handleNavBtnMouseLeave} aria-label="Log out" title="Log out">
              <svg width="30px" height="30px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                <path d="M15 12L6 12M6 12L8 14M6 12L8 10" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 21.9827C10.4465 21.9359 9.51995 21.7626 8.87865 21.1213C8.11027 20.3529 8.01382 19.175 8.00171 17M16 21.9983C18.175 21.9862 19.3529 21.8897 20.1213 21.1213C21 20.2426 21 18.8284 21 16V14V10V8C21 5.17157 21 3.75736 20.1213 2.87868C19.2426 2 17.8284 2 15 2H14C11.1715 2 9.75733 2 8.87865 2.87868C8.11027 3.64706 8.01382 4.82497 8.00171 7" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M3 9.5V14.5C3 16.857 3 18.0355 3.73223 18.7678C4.46447 19.5 5.64298 19.5 8 19.5M3.73223 5.23223C4.46447 4.5 5.64298 4.5 8 4.5" stroke="#1C274C" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
          </>
        )}
      </nav>
    </header>
  );
};

const App = () => {
  // Preload face-api.js models when the app starts
  // Remove blocking model preloading from app startup
  // Models will be loaded lazily when face detection is needed
  useEffect(() => {
    // Start performance monitoring
    performanceMonitor.monitorWebVitals();
    performanceMonitor.logBundleInfo();
    
    // Optional: Preload models in background after app is ready
    const preloadModelsInBackground = async () => {
      try {
        // Wait for app to be fully loaded before starting model preload
        setTimeout(async () => {
          await performanceMonitor.measureAsync('Model Preloading', async () => {
            await modelPreloader.loadModelsLazy();
          });
        }, 2000); // Delay to ensure app is interactive first
      } catch (error) {
        console.warn('Background model preloading failed:', error);
      }
    };

    preloadModelsInBackground();
  }, []);

  return (
    <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
      <GlobalProvider>
        <Router>
          <TourProvider>
            <>
              {/* Skip Link for keyboard navigation - WCAG 2.4.1 */}
              <a href="#main-content" className="skip-link">
                Skip to main content
              </a>
              {/* Live region for screen reader announcements */}
              <div aria-live="polite" aria-atomic="true" className="aria-live-region" id="aria-live-region"></div>
              <Header />
              <main id="main-content" role="main">
                <Suspense fallback={<LoadingSpinner />}>
                  <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/logout" element={<LogoutHandler />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/signup-success" element={<SignupSuccess />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/data-protection-policy" element={<DataProtectionPolicy />} />
              <Route path="/eula" element={<EULA />} />
              
              {/* Protected routes */}
              <Route path="/" element={<ProtectedRoute><RedirectPage /></ProtectedRoute>} />
              <Route path="/list" element={<ProtectedRoute><CreateTest /></ProtectedRoute>} />
                <Route path="/test/:id" element={<TestPage />} />
                <Route path="/reset/:id" element={<ResetPage />} />
                <Route path="/result" element={<ProtectedRoute><SearchResult/></ProtectedRoute>} />
                <Route path="/createTemplate" element={<ProtectedRoute><CreateTemplate/></ProtectedRoute>} />
                <Route path="/profilerPage" element={<ProtectedRoute><ProfilerPage/></ProtectedRoute>} />
                <Route path="/aiinterview" element={<ProtectedRoute><AIInterview/></ProtectedRoute>} />
                <Route path="/interviewPage" element={<ProtectedRoute><InterviewPage/></ProtectedRoute>} />
                <Route path="/speechtotext" element={<ProtectedRoute><SpeechToText/></ProtectedRoute>} />
                <Route path="/chatBot" element={<ProtectedRoute><ChatBot/></ProtectedRoute>} />
                <Route path="/faceDetection" element={<ProtectedRoute><FaceDetection/></ProtectedRoute>} />
                <Route path="/edit/:id" element={<ProtectedRoute><EditTemplate/></ProtectedRoute>} />
                <Route path='/profile' element={<ProtectedRoute><Profile/></ProtectedRoute>} />
                <Route path="/createJD" element={<ProtectedRoute><CreateJD /></ProtectedRoute>} />
                <Route path="/createTemplateFromJD" element={<ProtectedRoute><CreateTemplateFromJD /></ProtectedRoute>} />
                <Route path="/cloneTemplates" element={<ProtectedRoute><CloneTemplates /></ProtectedRoute>} />
                <Route path="/admin" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
                  </Routes>
                </Suspense>
              </main>
            </>
          </TourProvider>
        </Router>
      </GlobalProvider>
    </GoogleReCaptchaProvider>
  );
};

export default App;
